"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from "react";
import { Engine, Render, Runner, Bodies, Body, Composite, Mouse, MouseConstraint } from "matter-js";
import { Typography } from "@repo/ui/aops-components/typography";
import styles from "./FourOhFour.module.css";
import { DiscoveryButton, type DiscoveryButtonProps } from "@repo/ui/aops-components/DiscoveryButton";
import stickerSvgs from "./stickers";

// the size of the walls and the floor. Make them very big so you can't
// drag items through them. They are positioned so they extend off screen
// beyond the canvas bounds, so you don't see them at all.
const BOUNDARY_THICKNESS = 1000;
const BOUNDARY_LENGTH = 10000;

// Stickers are authored at this multiple of on-canvas size so the browser
// rasterizes a denser bitmap. We scale sprites and bodies back down by the
// same factor when adding them to the world.
const STICKER_TEXTURE_SCALE = 3;

// A random spin and x velocity is applied to each sticker so they don't fall uniformly
const MAX_ANGULAR_SPEED = 0.1;
const MAX_X_SPEED = 20;

/**
 * Calculate the width and height of the matter.js canvas,
 * based on the screen width and the height of the header element
 *
 * @param headerId - The ID of the header element
 * @returns The width and height of the canvas
 */
function getCanvasSize(headerId: string) {
  return {
    canvasWidth: window.innerWidth,
    canvasHeight: window.innerHeight - (document.getElementById(headerId)?.clientHeight ?? 0)
  };
}

/**
 * Canvas 2D defaults `imageSmoothingQuality` to `"low"`. Assigning
 * `canvas.width`/`height` (which `Render.setSize` does) resets the context,
 * so this must be reapplied after every resize.
 */
function applyImageSmoothing(render: Render) {
  render.context.imageSmoothingEnabled = true;
  render.context.imageSmoothingQuality = "high";
}
function isUnusableCanvasImage(source: CanvasImageSource): boolean {
  return source instanceof HTMLImageElement && (!source.complete || source.naturalWidth === 0);
}

/**
 * Matter.js calls `drawImage` every frame. A sticker whose URL 404s (bots,
 * missing `/discover` basePath, flaky static assets) puts the image in the
 * broken state and throws `InvalidStateError`, which Datadog records as a
 * JS error. Skip those frames instead of crashing the render loop.
 */
function guardCanvasDrawImage(ctx: CanvasRenderingContext2D) {
  ctx.drawImage = function (this: CanvasRenderingContext2D, image: CanvasImageSource, ...rest: number[]) {
    if (isUnusableCanvasImage(image)) {
      return;
    }
    try {
      CanvasRenderingContext2D.prototype.drawImage.apply(this, [image, ...rest] as never);
    } catch {
      // Broken or tainted textures must not kill the physics loop.
    }
  } as typeof ctx.drawImage;
}
function waitForImage(src: string): Promise<boolean> {
  return new Promise(resolve => {
    const img = new Image();
    let settled = false;
    const finish = (ok: boolean) => {
      if (settled) {
        return;
      }
      settled = true;
      resolve(ok);
    };
    img.onload = () => finish(true);
    img.onerror = () => finish(false);
    img.src = src;
    if (img.complete) {
      finish(img.naturalWidth > 0);
    }
  });
}

/**
 * Update the size of the matter.js canvas
 * @param render
 * @param width
 * @param height
 */
function setRenderSize(render: Render, width: number, height: number) {
  Render.setSize(render, width, height);
  render.canvas.style.width = `${width}px`;
  render.canvas.style.height = `${height}px`;
  applyImageSmoothing(render);
}

/**
 * Update the positions of the boundary walls + floor to match a new canvas size
 * @param floor
 * @param leftWall
 * @param rightWall
 * @param width
 * @param height
 */
function setBoundaryPositions(floor: Body, leftWall: Body, rightWall: Body, width: number, height: number) {
  Body.setPosition(floor, {
    x: width / 2,
    y: height + BOUNDARY_THICKNESS / 2
  });
  Body.setPosition(leftWall, {
    x: -BOUNDARY_THICKNESS / 2,
    y: height / 2
  });
  Body.setPosition(rightWall, {
    x: width + BOUNDARY_THICKNESS / 2,
    y: height / 2
  });
}

/**
 * Re-run `onChange` when the header appears, is replaced (Suspense
 * fallback → real header), or changes size. Window resize is handled
 * separately by the caller.
 */
function observeHeader(headerId: string, onChange: () => void) {
  let observedHeader: Element | null = null;
  const resizeObserver = new ResizeObserver(onChange);
  const attach = () => {
    const header = document.getElementById(headerId);
    if (header === observedHeader) {
      return;
    }
    if (observedHeader) {
      resizeObserver.unobserve(observedHeader);
    }
    observedHeader = header;
    if (header) {
      resizeObserver.observe(header);
      onChange();
    }
  };
  const mutationObserver = new MutationObserver(attach);
  mutationObserver.observe(document.body, {
    childList: true,
    subtree: true
  });
  attach();
  return () => {
    mutationObserver.disconnect();
    resizeObserver.disconnect();
  };
}

/**
 * The default button props for the 404 canvas. If no button props
 * are provided, this button will be shown. Any props provided will
 * override these defaults
 */
const defaultButton: DiscoveryButtonProps["button"] = {
  _type: "button",
  link: "/",
  text: "Go back home",
  icon: "arrow-left",
  iconPosition: "left",
  variant: "default",
  size: "lg"
};

/**
 * @param headerId - The ID of the header element, used to calulate the canvas height
 * @param button - a partial Sanity button object to override any default button props
 */
export type FourOhFourProps = {
  headerId?: string;
  button?: Partial<DiscoveryButtonProps["button"]>;
};

/**
 * Our fun little 404 page :)
 *
 * Uses matter.js to create a canvas with a random assortment of stickers,
 * and a mouse constraint to allow the user to interact with the stickers.
 *
 * The stickers are randomly placed above the top of the canvas, with a random
 * rotation, angular velocity, and x velocity, so they all fall into view in a
 * distinct, non-uniform way.
 *
 * On mount, the canvas size is calculated based on the screen size and the header height.
 * The goal is to have the canvas fill the visual screen space. The canvas will resize
 * when the window changes sizes, and when the header appears or changes height
 * (e.g. a Suspense fallback replaced by the real header).
 */
export function FourOhFour(t0) {
  const $ = _c(8);
  const {
    headerId: t1,
    button: t2
  } = t0;
  const headerId = t1 === undefined ? "discovery-header" : t1;
  const button = t2 === undefined ? defaultButton : t2;
  const sceneRef = useRef(null);
  let t3;
  if ($[0] !== button) {
    t3 = {
      ...defaultButton,
      ...button
    };
    $[0] = button;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const renderedButton = t3;
  let t4;
  let t5;
  if ($[2] !== headerId) {
    t4 = () => {
      const scene = sceneRef.current;
      if (!scene) {
        return;
      }
      let cancelled = false;
      let teardown;
      (async () => {
        const loadedTextures = new Set();
        const loadResults = await Promise.all(stickerSvgs.map(_temp));
        for (const {
          src: src_0,
          ok
        } of loadResults) {
          if (ok) {
            loadedTextures.add(src_0);
          }
        }
        if (cancelled || !sceneRef.current) {
          return;
        }
        const {
          canvasWidth,
          canvasHeight
        } = getCanvasSize(headerId);
        const engine = Engine.create();
        const render = Render.create({
          element: scene,
          engine,
          options: {
            width: canvasWidth,
            height: canvasHeight,
            pixelRatio: window.devicePixelRatio,
            wireframes: false,
            background: "inherit"
          }
        });
        applyImageSmoothing(render);
        guardCanvasDrawImage(render.context);
        Render.run(render);
        const runner = Runner.create();
        Runner.run(runner, engine);
        const isMobile = canvasWidth < 800;
        const scale = (isMobile ? 0.6666666666666666 : 1) / STICKER_TEXTURE_SCALE;
        const stickers = stickerSvgs.map(t6 => {
          const {
            width,
            height,
            src: src_1
          } = t6;
          const x = Math.random() * canvasWidth;
          const y = -500 - Math.random() * 500;
          return Bodies.rectangle(x, y, width * scale, height * scale, {
            restitution: 0.9,
            friction: 1,
            angle: Math.random() * Math.PI * 2,
            render: loadedTextures.has(src_1) ? {
              sprite: {
                texture: src_1,
                xScale: scale,
                yScale: scale
              }
            } : {}
          });
        });
        stickers.forEach(_temp2);
        const floor = Bodies.rectangle(canvasWidth / 2, canvasHeight + BOUNDARY_THICKNESS / 2, BOUNDARY_LENGTH, BOUNDARY_THICKNESS, {
          isStatic: true
        });
        const leftWall = Bodies.rectangle(-BOUNDARY_THICKNESS / 2, canvasHeight / 2, BOUNDARY_THICKNESS, BOUNDARY_LENGTH, {
          isStatic: true
        });
        const rightWall = Bodies.rectangle(canvasWidth + BOUNDARY_THICKNESS / 2, canvasHeight / 2, BOUNDARY_THICKNESS, BOUNDARY_LENGTH, {
          isStatic: true
        });
        Composite.add(engine.world, [...stickers, floor, leftWall, rightWall]);
        const mouse = Mouse.create(render.canvas);
        const mouseConstraint = MouseConstraint.create(engine, {
          mouse,
          constraint: {
            stiffness: 0.5,
            render: {
              visible: false
            }
          }
        });
        Composite.add(engine.world, mouseConstraint);
        render.mouse = mouse;
        const syncSize = () => {
          const next = getCanvasSize(headerId);
          setRenderSize(render, next.canvasWidth, next.canvasHeight);
          guardCanvasDrawImage(render.context);
          setBoundaryPositions(floor, leftWall, rightWall, next.canvasWidth, next.canvasHeight);
        };
        window.addEventListener("resize", syncSize);
        const stopObservingHeader = observeHeader(headerId, syncSize);
        const stop = () => {
          window.removeEventListener("resize", syncSize);
          stopObservingHeader();
          Render.stop(render);
          Runner.stop(runner);
          Composite.clear(engine.world, false);
          Engine.clear(engine);
          render.canvas.remove();
        };
        if (cancelled) {
          stop();
          return;
        }
        teardown = stop;
      })();
      return () => {
        cancelled = true;
        teardown?.();
      };
    };
    t5 = [headerId];
    $[2] = headerId;
    $[3] = t4;
    $[4] = t5;
  } else {
    t4 = $[3];
    t5 = $[4];
  }
  useEffect(t4, t5);
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className={styles.messageHeader}><Typography variant="stat">404</Typography><Typography variant="bodyXl">The page you're looking for doesn't exist</Typography></div>;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== renderedButton) {
    t7 = <div ref={sceneRef} className={styles.canvasContainer}><div className={styles.messageContainer}>{t6}<DiscoveryButton button={renderedButton} /></div></div>;
    $[6] = renderedButton;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  return t7;
}
function _temp2(sticker) {
  Body.setAngularVelocity(sticker, (Math.random() - 0.5) * MAX_ANGULAR_SPEED * 2);
  Body.setVelocity(sticker, {
    x: (Math.random() - 0.5) * MAX_X_SPEED * 2,
    y: 0
  });
}
async function _temp(t0) {
  const {
    src
  } = t0;
  return {
    src,
    ok: await waitForImage(src)
  };
}